import { fabric } from 'fabric';
import { IEvent, Text as FabricText, Textbox } from 'fabric/fabric-impl';
import { AlignCenter, AlignLeft, AlignRight } from 'lucide-react';
import { Trans, useLingui } from '@lingui/react/macro';
import React, { useCallback, useEffect, useState } from 'react';
import { toFiniteNumber } from 'shared/utils';
import alignment from 'modules/overlay-canvas/addons/alignment/alignment';
import history from 'modules/overlay-canvas/addons/history';
import SnapToGrid from 'modules/overlay-canvas/addons/snap-to-grid';
import UDTippy from 'ud-ui/components/tippy';
import { nearestWeight, normalizeFontWeight, primaryFontFamily } from 'shared/fonts';
import { round } from 'shared/utils/round';
import ColorPicker from 'modules/template-editor/ui/components/elements/color-picker';
import Input from 'modules/template-editor/ui/components/elements/input';
import LayerControls from './components/layer-controls';
import TeamColorSource from './components/team-color-source';
import LockPosition from './components/lock-position';
import UDEmptyButton from 'modules/ud-ui/components/empty-button';
import FontFamilySelect from './shared/font-family-select';
import FontWeightSelect from './shared/font-weight-select';
import { redrawAfterFonts } from './shared/redraw-after-fonts';
import TransformSection from './shared/transform-section';
import { subscribeLiveTransform } from './shared/use-live-transform';
import { Item, LongDivider, ShortDivider, SubItem } from './styles';
import { useEditorManager } from 'modules/template-editor/ui/context';

interface IProps {
  text: Textbox | FabricText;
}

function Text(props: IProps) {
  const { t } = useLingui();
  const text = props.text;
  const manager = useEditorManager();
  const lockMovementX = text?.lockMovementX;
  const [color, setColor] = React.useState(text.fill?.toString());
  const [fontSize, setFontSize] = React.useState(text.fontSize);
  const [fontWeight, setFontWeight] = React.useState(text.fontWeight);
  const [fontFamily, setFontFamily] = React.useState(text.fontFamily);
  const [scale, setScale] = React.useState(text.scaleY);
  const [position, setPosition] = useState({ x: text.left ?? 0, y: text.top ?? 0 });
  const [size, setSize] = useState({ width: round(text.width ?? 0), height: round(text.height ?? 0) });
  const [isPositionLock, setIsPositionLock] = useState(lockMovementX ?? false);

  useEffect(() => {
    setColor(text.fill?.toString());
    setFontSize(text.fontSize);
    setFontWeight(text.fontWeight);
    setFontFamily(text.fontFamily);
    setScale(text.scaleY);
    setPosition({ x: text.left ?? 0, y: text.top ?? 0 });
    setSize({ width: round(text.width ?? 0), height: round(text.height ?? 0) });
    text.setControlsVisibility({ tl: false, tr: false, br: false, bl: false });
    setIsPositionLock(text.lockMovementX ?? false);

    const onScale = (e: IEvent<Event>) => {
      if (e.transform?.corner === 'ml') {
        if (text.width === undefined || text.left === undefined) return;
        let tl = SnapToGrid.roundByGrid(text.left ?? 0);
        if (!tl) return;
        text.set({ left: tl, width: text.width + text.left - tl });
      }

      if (e.transform?.corner === 'mr') {
        const width = text.width ?? 0;
        text.set({ width: SnapToGrid.roundByGrid(width), scaleX: 1 });
      }
    };

    const updateScaling = () => {
      setScale(text.scaleY ?? 1);
    };

    let updatePosition = () => {
      const x = text?.left ?? 0;
      const y = text?.top ?? 0;
      setPosition({ x: round(x), y: round(y) });
    };

    let updateSize = () => {
      const { width, height } = text?.getBoundingRect(true) ?? defaultBounds;
      setSize({ width: round(width), height: round(height) });
    };

    const offPosition = subscribeLiveTransform(manager?.canvas, updatePosition);
    const offSize = subscribeLiveTransform(manager?.canvas, updateSize);
    text.on('resizing', onScale);
    text.on('scaling', updateScaling);
    return () => {
      text.off('resizing', onScale);
      text.off('scaling', updateScaling);
      offPosition();
      offSize();
    };
  }, [manager?.canvas, text]);

  const onChangeColor = (color: string) => {
    setColor(color);
    text.set({ fill: color });
    manager?.render();
    history.saveState();
  };

  const onChangeFontSize = (value: number) => {
    if (isNaN(value)) return;
    setFontSize(value);
    text.set({ fontSize: value });
    manager?.render();
  };

  const onChangeFontFamily = (value: string) => {
    setFontFamily(value);
    text.set({ fontFamily: value });
    // Веса, которого у нового шрифта нет, — ближайший настоящий, иначе браузер синтезирует жирность.
    const weight = nearestWeight(primaryFontFamily(value), fontWeight);
    if (weight !== normalizeFontWeight(fontWeight, primaryFontFamily(value))) onChangeFontWeight(weight);
    manager?.render();
    void redrawAfterFonts([text], () => manager?.render());
  };
  const onChangeFontWeight = (value: number) => {
    setFontWeight(value);
    text.set({ fontWeight: value });
    manager?.render();
  };

  const onChangeTextAlign = (value: string) => {
    text.set({ textAlign: value });
    manager?.render();
  };

  const onClickAlignmentCenterVertical = () => {
    const newPos = alignment.single.centerVertical(text);
    if (!newPos) return;
    setPosition({ x: newPos.left, y: newPos.top });
  };

  const onClickAlignmentCenterHorizontal = () => {
    const newPos = alignment.single.centerHorizontal(text);
    if (!newPos) return;
    setPosition({ x: newPos.left, y: newPos.top });
  };

  const onFocusField = useCallback(() => {
    manager?.render();
    history.saveState();
  }, [manager]);

  const onChangePosition = useCallback(
    (x: number, y: number) => {
      text.setPositionByOrigin(new fabric.Point(x, y), 'left', 'top');
      setPosition({ x: round(x), y: round(y) });
      manager?.render();
    },
    [manager, text],
  );

  const onChangeSize = useCallback(
    (width: number, height: number) => {
      text.set({ scaleX: width / (text?.width ?? 1), scaleY: height / (text?.height ?? 1) });
      setSize({ width: round(width), height: round(height) });
      SnapToGrid.transformAfterScaling(text);
      manager?.render();
    },
    [manager, text],
  );

  const scaleY = (scale ?? 1).toFixed(4);

  return (
    <>
      <Item>
        <div>{t`Текст`}</div>
      </Item>
      <ShortDivider />
      <TransformSection
        position={position}
        size={size}
        onChangePosition={onChangePosition}
        onChangeSize={onChangeSize}
        onAlignCenterHorizontal={onClickAlignmentCenterHorizontal}
        onAlignCenterVertical={onClickAlignmentCenterVertical}
        onFocusField={onFocusField}
      >
        <LongDivider />
        <span>{t`Тип шрифта`}</span>
        <FontFamilySelect value={fontFamily} fontWeight={fontWeight} onChange={onChangeFontFamily} />
        <SubItem style={{ display: 'flex', gap: 8, justifyContent: 'space-between' }}>
          <FontWeightSelect className={'w-full'} fontFamily={fontFamily} value={fontWeight} onChange={onChangeFontWeight} />
          <Input
            numeric
            style={{ width: 55 }}
            unit="px"
            onChange={(value) => {
              onChangeFontSize(toFiniteNumber(value, fontSize ?? 20));
            }}
            value={fontSize?.toString() ?? '20'}
          />
        </SubItem>
      </TransformSection>
      <LongDivider />
      <TeamColorSource object={text} title={t`Цвет шрифта`} allowContrast>
        <ColorPicker color={color ?? '#000000'} onChange={onChangeColor} showAlpha />
      </TeamColorSource>
      <LongDivider />
      <Item style={{ flexDirection: 'row', gap: 8, justifyContent: 'left' }}>
        <UDTippy content={t`По левому краю`} size="xs" placement="bottom" delay={300}>
          <span style={{ display: 'inline-block' }}>
            <UDEmptyButton
              onClick={() => {
                onChangeTextAlign('left');
              }}
            >
              <AlignLeft size={20} />
            </UDEmptyButton>
          </span>
        </UDTippy>
        <UDTippy content={t`По центру`} size="xs" placement="bottom" delay={300}>
          <span style={{ display: 'inline-block' }}>
            <UDEmptyButton
              onClick={() => {
                onChangeTextAlign('center');
              }}
            >
              <AlignCenter size={20} />
            </UDEmptyButton>
          </span>
        </UDTippy>
        <UDTippy content={t`По правому краю`} size="xs" placement="bottom" delay={300}>
          <span style={{ display: 'inline-block' }}>
            <UDEmptyButton
              onClick={() => {
                onChangeTextAlign('right');
              }}
            >
              <AlignRight size={20} />
            </UDEmptyButton>
          </span>
        </UDTippy>
      </Item>
      <LongDivider />
      <LayerControls object={text} />
      <LockPosition objects={[text]} onChange={setIsPositionLock} value={isPositionLock} />
      <Item>
        <SubItem>
          <Trans>Размер по Y: {scaleY}</Trans>
        </SubItem>
      </Item>
    </>
  );
}

export default Text;

const defaultBounds = {
  x: 0,
  y: 0,
  width: 0,
  height: 0,
};
